import { ENV } from '@/config/env';

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY = 500;
// Los reintentos con backoff suman ~7,5s de espera, así que el techo va bien por
// encima para no cortar un intento legítimo.
const TIMEOUT = 20000;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
const getRetryDelay = (retryCount: number): number => BASE_RETRY_DELAY * 2 ** retryCount;

const runReCaptcha = (retryCount = 0): Promise<string> =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.grecaptcha) {
      reject(new Error('reCAPTCHA is not loaded'));
      return;
    }

    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha?.execute(ENV.GOOGLE_SITE_KEY || '', {
          action: 'submit',
        });

        if (!token) {
          reject(new Error('reCAPTCHA returned empty token'));
          return;
        }
        resolve(token);
      } catch (error) {
        console.error(`Failed to execute reCAPTCHA (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`, error);

        if (retryCount < MAX_RETRIES) {
          try {
            await wait(getRetryDelay(retryCount));
            const token = await runReCaptcha(retryCount + 1);

            resolve(token);
          } catch (retryError) {
            reject(retryError);
          }
        } else {
          reject(new Error('reCAPTCHA failed after maximum retries'));
        }
      }
    });
  });

// grecaptcha.ready() no ejecuta su callback si el script quedó a medio cargar, y ahí
// la promesa no resuelve ni rechaza nunca: el botón de agendar quedaría bloqueado sin
// salida. El techo de tiempo la manda al catch que ya muestra el diálogo de error.
export const executeReCaptcha = (): Promise<string> => {
  let timeoutId: ReturnType<typeof setTimeout>;

  const timeout = new Promise<string>((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new Error('reCAPTCHA timed out')), TIMEOUT);
  });

  return Promise.race([runReCaptcha(), timeout]).finally(() => clearTimeout(timeoutId));
};
